/**
 * Confirming an allowance right after the approve that set it (app#323).
 *
 * The approve is mined — its receipt is in hand — and the allowance is read back before the call
 * that spends it, so a cap the buyer lowered in the wallet fails with a sentence instead of a
 * revert with nothing to read. On a load-balanced public RPC the node that answers the read is not
 * always the node that saw the block: a lagging one returns the allowance *before* the approve,
 * and a correct approve was accused of being too low — "Pay did not work the first time; the second
 * time it did" (Tim, 2026-10-15; reproduced: the first attempt ended after one read, the second
 * passed with the allowance long set). A read right after a write is retried, on every chain
 * ([[nile-trongrid-eventual-consistency-reads]]); only a value that stays low is the buyer's.
 */

export interface AwaitAllowanceDeps {
  /** One read of the allowance, as the chain reports it (base units). */
  read: () => Promise<bigint>;
  /** What the call will pull. */
  amount: bigint;
  attempts?: number;
  intervalMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

export const ALLOWANCE_ATTEMPTS = 6;
export const ALLOWANCE_POLL_MS = 2_000;

export class UnderApprovedError extends Error {
  constructor(
    public readonly allowance: bigint,
    public readonly amount: bigint
  ) {
    super(
      'The amount you approved in your wallet is lower than required. Please approve the full amount shown in the app and try again.'
    );
    this.name = 'UnderApprovedError';
  }
}

/** Every read failed: nothing is known about the allowance, and the buyer is not to blame. */
export class AllowanceUnreadableError extends Error {
  constructor() {
    super('The allowance could not be read from the network. Please try again in a moment.');
    this.name = 'AllowanceUnreadableError';
  }
}

const pause = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Resolves with the allowance once a read shows at least `amount`; `UnderApprovedError` when none does. */
export const awaitAllowance = async (deps: AwaitAllowanceDeps): Promise<bigint> => {
  const { read, amount, attempts = ALLOWANCE_ATTEMPTS, intervalMs = ALLOWANCE_POLL_MS, sleep = pause } = deps;
  let last = 0n;
  let answered = false;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    // A node that errors on the read (a rate limit, a hiccup) is asked again like a lagging one.
    try {
      last = await read();
      answered = true;
    } catch {
      /* asked again below */
    }

    if (answered && last >= amount) {
      return last;
    }

    if (attempt < attempts) {
      await sleep(intervalMs);
    }
  }

  if (!answered) {
    // Not one read came back: that is the network, not the buyer's cap.
    throw new AllowanceUnreadableError();
  }

  throw new UnderApprovedError(last, amount);
};
